// import { refreshTokenUser } from '@/api';
import { useAuthStore } from '@/stores/authStore';
import useLanguageStore from '@/stores/useLanguageStore';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_URL, API_VERSION } from './setup.config';
import { useUserStore } from '@/stores';
import { ROUTE } from './routes.config';
import * as Sentry from '@sentry/react';

export const apiConfig = {
  BASE_URL: `${API_URL}/${API_VERSION}`,
};

const setAuthHeader = (config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
};

const setLangAsParams = (config: InternalAxiosRequestConfig) => {
  const lang = useLanguageStore.getState().language || 'en';
  if (config.params) {
    config.params.lang = lang;
  } else {
    config.params = { lang };
  }
  return config;
};

// refresh token
// const refreshToken = async () => {
//   const { setToken, clearToken } = useAuthStore.getState();
//   const { setUser } = useUserStore.getState();

//   try {
//     const result = await refreshTokenUser();
//     setToken(result?.object?.access_token);
//     return result?.object?.access_token;
//   } catch (error) {
//     clearToken();
//     setUser(undefined);
//     return false;
//   }
// };

const createAxiosInstance = (contentType: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: apiConfig.BASE_URL,
    headers: {
      'Content-Type': contentType,
      'X-Client-Type': 'web',
      //'Document-Policy': 'js-profiling'
    },
  });

  instance.interceptors.request.use(setLangAsParams);

  instance.interceptors.request.use(setAuthHeader);

  // Add Sentry breadcrumb for API requests
  instance.interceptors.request.use((config) => {
    Sentry.addBreadcrumb({
      category: 'api.request',
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
        params: config.params,
      },
    });
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      // Add successful response breadcrumb
      Sentry.addBreadcrumb({
        category: 'api.response',
        message: `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
        level: 'info',
        data: {
          url: response.config.url,
          method: response.config.method,
          status: response.status,
        },
      });
      return response;
    },
    async (error) => {
      const url = error?.config?.url;
      const method = error?.config?.method?.toUpperCase();
      const status = error?.response?.status;

      // Add Sentry context for API errors
      Sentry.addBreadcrumb({
        category: 'api.error',
        message: `${method} ${url} - ${status || 'Network Error'}`,
        level: 'error',
        data: {
          url,
          method,
          status,
          statusText: error?.response?.statusText,
          errorMessage: error?.response?.data?.message,
        },
      });

      // Add context to the error for Sentry
      // Safely parse request data - it might be FormData, object, or JSON string
      let request_data;
      try {
        if (error?.config?.data) {
          // If it's already an object (including FormData), use it as is
          if (typeof error.config.data === 'object') {
            request_data = error.config.data instanceof FormData ? '[FormData]' : error.config.data;
          } else if (typeof error.config.data === 'string') {
            // Only parse if it's a string
            request_data = JSON.parse(error.config.data);
          }
        }
      } catch (parseError) {
        // If parsing fails, just use the raw data as string
        request_data = String(error?.config?.data);
      }

      Sentry.setContext('api_error', {
        endpoint: url,
        method,
        status,
        request_data,
        response_data: error?.response?.data,
        params: error?.config?.params,
      });

      // TODO: skipRedirectUrls for this endpoint was added due to an issue with Zendesk (as Hamid mentioned).
      // ! Once the issue is resolved, this restriction should be removed and redirect should apply to all routes.
      const skipRedirectUrls = ['/merchants/tickets'];

      // const originalRequest = error.config;
      // originalRequest._retryCount = originalRequest._retryCount || 0;

      if (error?.response?.status === 401) {
        const { clearToken } = useAuthStore.getState();
        const { clearUser } = useUserStore.getState();

        const shouldSkip = skipRedirectUrls.some((u) => url?.includes(u));

        if (!shouldSkip) {
          clearUser();
          clearToken();
          window.location.replace(ROUTE.LOGIN);
        }

        // originalRequest._retryCount += 1;
        // try {
        //   const newToken = await refreshToken();
        //   if (newToken) {
        //     originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        //     return instance(originalRequest);
        //   }
        // } catch (tokenError) {
        //   return Promise.reject(tokenError);
        // }
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export const axiosJSONInstance = createAxiosInstance('application/json');
export const axiosMultipartInstance = createAxiosInstance('multipart/form-data');
